import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '../../config/seo';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  keywords?: string[];
  pageType?: 'home' | 'article' | 'product' | 'contact' | 'about' | 'pricing' | 'docs';
  articleMeta?: {
    publishedTime: string;
    modifiedTime: string;
    authors: string[];
    tags: string[];
    section?: string;
  };
  productMeta?: {
    price: string;
    currency: string;
    availability: string;
  };
  faqItems?: Array<{
    question: string;
    answer: string;
  }>;
  breadcrumbs?: Array<{
    name: string;
    url: string;
  }>;
  jsonLd?: Record<string, any>[];
}

export const EnhancedSEO: React.FC<SEOProps> = ({
  title,
  description = siteConfig.description,
  canonical = siteConfig.siteUrl,
  ogImage = `${siteConfig.siteUrl}/og-image.png`,
  noindex = false,
  keywords = siteConfig.keywords,
  pageType = 'home',
  articleMeta,
  productMeta,
  faqItems,
  breadcrumbs,
  jsonLd,
}) => {
  // Format title according to page type
  const fullTitle = title 
    ? title + ' | ' + (siteConfig.title.split('|')[1] || 'Newsroom').trim()
    : siteConfig.title;
  
  // Generate keywords string if provided
  const keywordsString = keywords ? keywords.join(', ') : '';

  // Default JSON-LD for website
  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": siteConfig.title,
    "description": description,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "author": {
      "@type": "Organization",
      "url": siteConfig.organization.url,
      "logo": {
        "@type": "ImageObject",
        "url": siteConfig.organization.logo,
        "width": "192",
        "height": "192"
      }
    },
    "offers": {
      "@type": "Offer",
      "price": productMeta?.price || "0",
      "priceCurrency": productMeta?.currency || "USD"
    }
  };

  // Organization schema
  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": siteConfig.organization.name,
    "url": siteConfig.organization.url,
    "logo": {
      "@type": "ImageObject",
      "url": siteConfig.organization.logo,
      "width": "192",
      "height": "192"
    },
    "sameAs": [
      `https://twitter.com/${siteConfig.twitterHandle.replace('@', '')}`
    ]
  };

  // Add local business info if available
  const localBusinessJsonLd = siteConfig.localBusiness ? {
    "@context": "https://schema.org",
    "@type": siteConfig.localBusiness.type,
    "name": siteConfig.localBusiness.name,
    "description": siteConfig.localBusiness.description,
    "url": siteConfig.organization.url,
    "areaServed": siteConfig.localBusiness.areaServed,
    "serviceType": siteConfig.localBusiness.serviceType
  } : null;

  // FAQ schema (if FAQ items are provided)
  const faqJsonLd = faqItems ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // Breadcrumb schema
  const breadcrumbJsonLd = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  } : null;

  // Combine all schemas based on what's available
  const schemas = [
    websiteJsonLd,
    organizationJsonLd,
    ...(localBusinessJsonLd ? [localBusinessJsonLd] : []),
    ...(faqJsonLd ? [faqJsonLd] : []),
    ...(breadcrumbJsonLd ? [breadcrumbJsonLd] : []),
    ...(jsonLd || [])
  ];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={siteConfig.language} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywordsString && <meta name="keywords" content={keywordsString} />}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />
      <meta name="author" content={siteConfig.author.name} />

      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="theme-color" content="#1E40AF" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

      {/* Open Graph / Facebook */}
      <meta property="og:site_name" content={siteConfig.title.split('|')[0].trim()} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={articleMeta ? 'article' : 'website'} />
      <meta property="og:locale" content={siteConfig.language} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={siteConfig.twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:creator" content={siteConfig.twitterHandle} />
      
      {/* Alternate Languages */}
      {siteConfig.alternateLanguages?.map(lang => (
        <link 
          key={lang.code} 
          rel="alternate" 
          hrefLang={lang.code} 
          href={lang.url} 
        />
      ))}

      {/* Article Meta */}
      {articleMeta && (
        <>
          <meta property="article:published_time" content={articleMeta.publishedTime} />
          <meta property="article:modified_time" content={articleMeta.modifiedTime} />
          {articleMeta.section && (
            <meta property="article:section" content={articleMeta.section} />
          )}
          {articleMeta.authors.map(author => (
            <meta key={author} property="article:author" content={author} />
          ))}
          {articleMeta.tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Product Meta */}
      {productMeta && (
        <>
          <meta property="product:price:amount" content={productMeta.price} />
          <meta property="product:price:currency" content={productMeta.currency} />
          <meta property="product:availability" content={productMeta.availability} />
        </>
      )}

      {/* JSON-LD Structured Data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default EnhancedSEO;
